import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, User, KeyRound } from "lucide-react";

/** Shared profile panel — change display name + password. Used as the
 *  "Profile" tab in both the seller and buyer dashboards. */
export default function ProfilePanel() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user?.name]);

  const saveName = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (n.length < 2) return toast.error("Please enter your full name");
    if (n === user?.name) return toast.info("That's already your name");
    setSavingName(true);
    try {
      const { data } = await api.patch("/me/profile", { name: n });
      setUser?.(data.user);
      toast.success("Name updated — buyers will see this on your storefront.");
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally { setSavingName(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("The two new passwords don't match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password, new_password: pw.new_password,
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed. We've emailed you a confirmation.");
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally { setSavingPw(false); }
  };

  return (
    <div className="max-w-xl space-y-5" data-testid="profile-panel">
      <form onSubmit={saveName} className="border border-border rounded-md bg-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-primary" />
          <h2 className="font-display font-semibold text-base">Your name</h2>
        </div>
        <p className="text-xs text-muted-foreground">This is the name buyers see on your designs and storefront.</p>
        <Field label="Full name" value={name} onChange={setName} testid="profile-name" />
        <Field label="Email (login — can't be changed)" value={user?.email || ""} onChange={() => {}} testid="profile-email" disabled />
        <button
          disabled={savingName}
          data-testid="profile-save-name"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
        >
          {savingName && <Loader2 className="h-4 w-4 animate-spin" />} Save name
        </button>
      </form>

      <form onSubmit={savePw} className="border border-border rounded-md bg-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" />
          <h2 className="font-display font-semibold text-base">Change password</h2>
        </div>
        {user?.auth_provider === "google" && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="profile-google-note">
            You sign in with Google. To set a Jeweltix password for the first time, use
            {" "}<a href="/forgot-password" className="underline font-semibold">Forgot password</a> — we'll email you a link.
          </p>
        )}
        <Field label="Current password" type="password" value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })} testid="profile-current-pw" />
        <Field label="New password (min 8 characters)" type="password" value={pw.new_password} onChange={(v) => setPw({ ...pw, new_password: v })} testid="profile-new-pw" />
        <Field label="Confirm new password" type="password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} testid="profile-confirm-pw" />
        <button
          disabled={savingPw}
          data-testid="profile-save-password"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
        >
          {savingPw && <Loader2 className="h-4 w-4 animate-spin" />} Update password
        </button>
      </form>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, type = "text", disabled }) => (
  <label className="block">
    <span className="text-sm font-medium">{label}</span>
    <input
      type={type}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full border border-border rounded-md p-2.5 text-base bg-background focus:outline-none focus:border-primary disabled:opacity-60"
    />
  </label>
);
